import type DialogOfferPrice from "@/components/marketplace/item/DialogOfferPrice.vue";
import type { MarketplaceItemId, OfferPriceConfig, OfferPriceSource } from "@/services/marketplace/item";
import { whenRefAvailable } from "@/utilities";
import type { Ref } from "vue";
import * as PostHog from "@/services/analytics/postHog";
import { useUser } from "@/composables/user";
import { createLoginLink } from "@/router";
import { useRoute, useRouter } from "vue-router";
import type { CollectionItemId } from "@/services/collection/item";

type PendingOpenAfterLoginConfig = {
  itemId: CollectionItemId | MarketplaceItemId;
  source: OfferPriceSource;
};

const pendingOpenAfterLogin = ref<PendingOpenAfterLoginConfig>();

export function useOfferPriceDialog(dialog: Ref<InstanceType<typeof DialogOfferPrice> | null>, options: OfferPriceConfig) {
  const { isUserLoggedIn } = useUser();

  const router = useRouter();
  const route = useRoute();

  const open = () => {
    PostHog.trackEvent(PostHog.EventName.Marketplace_OfferPriceOpenDialog_Click);

    if (isUserLoggedIn.value) {
      whenRefAvailable(dialog).then((d) => {
        d.value.open(options);
      });
    } else {
      requireLogin();
    }
  };

  const requireLogin = () => {
    const storeConfig = {
      itemId: options.itemId,
      source: options.source,
    };
    usePendingOfferPriceDialog(storeConfig).storePendingOpen();

    const redirect = route.fullPath;
    const loginRoute = createLoginLink(redirect);
    router.push(loginRoute);
  };

  return {
    open,
  };
}

type PendingOfferPriceDialogOptions = {
  itemId: CollectionItemId | MarketplaceItemId;
  source: OfferPriceSource;
};

export function usePendingOfferPriceDialog(options: PendingOfferPriceDialogOptions) {
  const isWaitingForLogin = computed(() => {
    const stored = pendingOpenAfterLogin.value;
    return stored !== undefined && stored.itemId === options.itemId && stored.source === options.source;
  });

  const storePendingOpen = () => {
    pendingOpenAfterLogin.value = {
      itemId: options.itemId,
      source: options.source,
    };
  };

  const clearPendingOpen = () => {
    pendingOpenAfterLogin.value = undefined;
  };

  return {
    isWaitingForLogin,
    storePendingOpen,
    clearPendingOpen,
  };
}
